import Big from "big.js";

/**
 * Derive a {price, tax} money pair from one of three entry modes. Shared by every
 * price-override surface (Add Override form, grid/Changes calc popover) so the
 * gross/net math lives in exactly one place. All outputs are rounded to 4 decimals
 * (the price/tax precision the override grids assume — see `fmtPrice`/`niceRound`).
 *
 *  - direct: price + tax entered as-is (today's behaviour).
 *  - net   (US): net price + tax rate% → tax = price·rate/100.
 *  - gross (EU): gross total + tax rate% → price = total/(1+rate/100), tax = total − price.
 *
 * Mirrors the bidirectional math already proven in TaxCalculatorTool.
 */
export type PriceTaxMode = "direct" | "net" | "gross";

export interface PriceTaxValue {
  price: number;
  tax: number;
}

const DP = 4;

function toFixedNum(b: Big): number {
  return Number(b.round(DP).toFixed(DP));
}

/** Parse a number string, "" / invalid → null. */
function parseNum(v: string | number | undefined): Big | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  try {
    return new Big(s);
  } catch {
    return null;
  }
}

/** Parse a possibly "%"-suffixed rate, "" / invalid / negative → null. */
function parseRate(v: string | number | undefined): Big | null {
  if (v == null) return null;
  let s = String(v).trim();
  if (s.endsWith("%")) s = s.slice(0, -1).trim();
  if (s === "") return null;
  try {
    const b = new Big(s);
    return b.lt(0) ? null : b;
  } catch {
    return null;
  }
}

export interface PriceTaxInputs {
  price?: string | number;
  tax?: string | number;
  total?: string | number;
  rate?: string | number;
}

/**
 * Compute {price, tax} for the given mode, or null when the required inputs are
 * missing/invalid (the caller then emits nothing). For `gross`, tax is taken as
 * `total − roundedPrice` so price + tax sums back to the entered total exactly.
 */
export function derivePriceTax(mode: PriceTaxMode, inputs: PriceTaxInputs): PriceTaxValue | null {
  if (mode === "direct") {
    const p = parseNum(inputs.price);
    const t = parseNum(inputs.tax);
    if (p == null && t == null) return null;
    return { price: p ? toFixedNum(p) : 0, tax: t ? toFixedNum(t) : 0 };
  }

  const rate = parseRate(inputs.rate);
  if (rate == null) return null;
  const factor = rate.div(100);

  if (mode === "net") {
    const p = parseNum(inputs.price);
    if (p == null) return null;
    return { price: toFixedNum(p), tax: toFixedNum(p.times(factor)) };
  }

  // gross
  const total = parseNum(inputs.total);
  if (total == null) return null;
  const price = total.div(factor.plus(1)).round(DP);
  const tax = total.minus(price);
  return { price: Number(price.toFixed(DP)), tax: toFixedNum(tax) };
}

/** Effective tax rate (%) for a price/tax pair, or null when price is 0/invalid. */
export function effectiveRate(price: number, tax: number): number | null {
  if (!Number.isFinite(price) || price === 0) return null;
  return Number(new Big(tax).div(new Big(price)).times(100).round(DP).toFixed(2));
}
